// The shield hit recipes, mockup round 051 (R1007).
//
// One entry per look id of `labels.toml hit_shield_styles`, keyed by the id.
// The layer picks this list while the victim's shields still hold (R1009). The
// rules of the file are the rules of `combat-recipes-melee.ts`.
//
// The mark is drawn on the CONTACT RING of the victim, which is the ring the
// fleet layer draws at `r_px` world px round a hull. The hull silhouette is not
// needed: a shield mark never touches the plate.
//
// ---------------------------------------------------------------------------
// THE SHIELD FRACTION (R1011)
// ---------------------------------------------------------------------------
//
// Three cards of the round — GROWING CRACK, LOW FLICKER and DRAIN SHIFT — read
// the victim's shield figure and say what is left of it. The mockup stage held
// no such figure, so those three read a 12 s saw off the page clock, and the
// page said the client reads the frame instead.
//
// `HitCtx.shieldFrac` is that frame: 0 to 1, what the victim's shields still
// hold, read off the victim's own state every frame and −1 where the viewer
// cannot read the contact. The three cards read it; the seven others do not.
// `DRAIN` below is the share that is GONE, which is what each of the three
// states, and a mark of an unread contact takes a full shield, because a
// contact the client cannot read says nothing about a failing shield.

import { FLEET_BARS } from '../gen/presentation';
import {
  COMBAT_WHITE, arcOf, backOf, clamp01, disc, dome, flare, hash, hexOutline,
  line, mixInk, out, polyline, ring, tail, zig,
  type HitCtx, type HitRecipeFn,
} from './combat-recipe-kit';

/**
 * How much of the victim's shield is GONE, 0 to 1 (R1011).
 *
 * A contact the viewer cannot read answers a FULL shield, because the client
 * has never seen that shield fail.
 */
function drain(c: HitCtx): number {
  return c.shieldFrac < 0 ? 0 : 1 - clamp01(c.shieldFrac);
}

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------

/** The shield white of §2. It is the one ink a shield mark wears, whatever
 *  faction the victim is: a shield is not a banner. */
const SHIELD_INK = FLEET_BARS.shieldColor;

/** ring-flare, card 01: the half-sweep of the lit arc, in radians. An arc's
 *  opening is the shape of the mark and not a figure a designer moves. */
const FLARE_ARC_HALF = 0.8;
/** ring-flare: the lit arc's floor width, as a share of its birth width. The
 *  card writes `2.5 - 1.5 * p`, so the arc thins to two fifths and no less. */
const FLARE_ARC_FLOOR = 0.4;

/** ring-ripple, card 02: how wide one running band opens, in radians. */
const RIPPLE_BAND = 0.5;
/** ring-ripple: how long the white flash on the impact point lives, as a share
 *  of the whole life. */
const RIPPLE_FLASH_SPAN = 0.3;
/** ring-ripple: where the two bands meet, as a share of the life. The rest of
 *  the life is the meeting flash on the far side. */
const RIPPLE_MEET_AT = 0.85;
/** ring-ripple: the alpha of the white flash and of the meeting flash. The row
 *  holds two alphas — the bands and the faint ring — and the card names four. */
const RIPPLE_FLASH_ALPHA = 0.8;
const RIPPLE_MEET_ALPHA = 0.7;

/** plate-lattice, card 03: the hex grid, as shares of one plate's radius. The
 *  columns stand 1.5 apart and the rows √3, which is what a hex grid is. */
const LATTICE_COL = 1.5;
const LATTICE_ROW = Math.sqrt(3);
/** plate-lattice: the turn of one plate, so a vertex faces along +x. */
const LATTICE_ROT = -Math.PI / 6;
/** plate-lattice: how far off the centre a plate may stand, as a share of the
 *  contact ring. The canvas card clipped the lattice to the ring; a batch has
 *  no clip, so the recipe drops a plate whose centre stands too far out and
 *  the edge is the plates themselves. */
const LATTICE_CUT = 0.9;
/** plate-lattice: a plate's lit disc, as a share of its radius. It is the hex
 *  inradius, so the disc touches all six sides — the nearest a circle batch
 *  comes to the card's filled hex. */
const LATTICE_INRADIUS = 0.866;
/** plate-lattice: how far a plate lights beside the struck one, as a share of
 *  a plate's radius, and how far it lights. */
const LATTICE_NEAR = 2.2;
const LATTICE_NEAR_LIT = 0.35;
/** plate-lattice: how far a lit plate raises its own hairline. */
const LATTICE_LIT_RISE = 0.5;
/** plate-lattice: the impact flash's life and alpha. */
const LATTICE_FLASH_SPAN = 0.35;
const LATTICE_FLASH_ALPHA = 0.7;

/** growing-crack, card 05: the turn between two cracks, in radians. */
const CRACK_FAN = 0.5;
/** growing-crack: where the cracks start to fade, as a share of the life. */
const CRACK_FADE_AT = 0.5;
/** growing-crack: the impact flash's life and alpha. */
const CRACK_FLASH_SPAN = 0.3;
const CRACK_FLASH_ALPHA = 0.8;
/** growing-crack: what the segment step is pulled back by, so a shield of
 *  exactly nothing still lands on `count2` and not one past it. It is the
 *  card's own `5.99` against a `count2` of six. */
const CRACK_STEP_EDGE = 0.01;

/** low-flicker, card 06: the half-sweep of the lit arc, in radians. */
const FLICKER_ARC_HALF = 0.6;
/** low-flicker: how far the stutter pulls the mark down. */
const FLICKER_DIM = 0.1;
/** low-flicker: what share of the row's `alpha2` the whole ring keeps at an
 *  EMPTY shield. The card writes `0.2 + 0.5 * s` over an alpha2 of 0.7, so the
 *  ring rests at two sevenths of its full figure. */
const FLICKER_RING_REST = 2 / 7;
/** low-flicker: which row of the hash the strobe reads. It is the card's own. */
const FLICKER_ROW = 6;
/** low-flicker: the bloom's alpha on the impact point. */
const FLICKER_FLARE_ALPHA = 0.6;

/** dome-sheen, card 12: where the dome starts to fade, as a share of the life. */
const SHEEN_FADE_AT = 0.3;
/** dome-sheen: the half-sweep of the sliding sheen, in radians. */
const SHEEN_HALF = 0.5;
/** dome-sheen: the rim ring's alpha, over the dome's own rim. */
const SHEEN_RIM_ALPHA = 0.5;
/** dome-sheen: the impact flash's life and alpha. */
const SHEEN_FLASH_SPAN = 0.3;
const SHEEN_FLASH_ALPHA = 0.7;

/** rim-sparks, card 14: how far off the impact point a spark may leave, in
 *  radians: the floor and the span a hash fills. */
const SPARK_ANG_MIN = 0.15;
const SPARK_ANG_SPAN = 1.1;
/** rim-sparks: how far behind itself a spark draws its tail, in radians and in
 *  world px. */
const SPARK_TAIL = 0.12;
const SPARK_DROP = 1;
/** rim-sparks: the half-sweep of the lit arc under the sparks, in radians. */
const SPARK_ARC_HALF = 0.4;
/** rim-sparks: the impact flash's life and alpha. */
const SPARK_FLASH_SPAN = 0.3;
const SPARK_FLASH_ALPHA = 0.8;

/** interference, card 15: how far one ripple stands under the one before it. */
const WAVE_STEP_ALPHA = 0.15;
/** interference: the impact flash's life and alpha. */
const WAVE_FLASH_SPAN = 0.3;
const WAVE_FLASH_ALPHA = 0.8;

/** rim-arcs, card 17: the turn of one step of a jagged arc, in radians. */
const ARC_STEP = 0.16;
/** rim-arcs: how tight an arc winds at birth, and how far it opens as the mark
 *  goes out. The card writes `0.6 + 0.4 * f`. */
const ARC_REST = 0.6;
const ARC_RISE = 0.4;
/** rim-arcs: what the frame counter is mixed by, so the two arcs of one frame
 *  do not wobble alike. It is the card's own `frame * 7 + s + 3`. */
const ARC_FRAME_MIX = 7;
/** rim-arcs: the most steps one arc may take. The scratch below is cut for it
 *  and the row's `count` is held under it, so nothing is allocated. */
const ARC_MAX_STEPS = 12;

/** The one reused scratch of this file: the joints of one jagged arc (R131). */
const ARC_XS = new Float64Array(ARC_MAX_STEPS + 1);
const ARC_YS = new Float64Array(ARC_MAX_STEPS + 1);

/**
 * Card 01 in one ink: the faint whole ring, the lit arc that faces the shooter
 * and the bloom on the impact point. RING FLARE draws it in the shield white
 * and DRAIN SHIFT in an ink that follows the shield figure.
 */
function ringFlare(c: HitCtx, ink: number): void {
  const r = c.row;
  const f = 1 - c.p;
  const th = backOf(c);
  ring(c, c.at.x, c.at.y, r.rPx, r.width2Px, ink, r.alpha2 * f);
  arcOf(
    c, c.at.x, c.at.y, r.rPx,
    r.widthPx * (1 - (1 - FLARE_ARC_FLOOR) * c.p),
    th - FLARE_ARC_HALF, FLARE_ARC_HALF * 2, ink, r.alpha * f,
  );
  flare(
    c, c.at.x + Math.cos(th) * r.rPx, c.at.y + Math.sin(th) * r.rPx,
    r.r2Px, ink, r.alpha2 * 2 * f,
  );
}

export const RECIPES: Readonly<Record<string, HitRecipeFn>> = {
  /**
   * Card 01, RING FLARE (the AUTO look).
   *
   * The arc that faces the shooter lights in the shield white, a bloom sits on
   * the impact point, and the whole ring shows faintly while the flare lives.
   */
  'ring-flare'(c) {
    ringFlare(c, SHIELD_INK);
  },

  /**
   * Card 02, RING RIPPLE.
   *
   * Two bright bands leave the impact point and run round the ring both ways.
   * They meet on the far side with a small flash. The shield takes the hit as
   * a wave, not as a spot.
   */
  'ring-ripple'(c) {
    const r = c.row;
    const f = 1 - c.p;
    const th = backOf(c);
    const sweep = Math.PI * out(c.p);
    ring(c, c.at.x, c.at.y, r.rPx, r.width2Px, SHIELD_INK, r.alpha2 * f);
    // The two bands trail their own leading edge, so each one reads as a wave
    // that runs and not as an arc that opens.
    for (let s = 1; s >= -1; s -= 2) {
      const a = th + s * sweep;
      arcOf(
        c, c.at.x, c.at.y, r.rPx, r.widthPx,
        s > 0 ? a - RIPPLE_BAND : a, RIPPLE_BAND, SHIELD_INK, r.alpha * f,
      );
    }
    const flash = 1 - c.p / RIPPLE_FLASH_SPAN;
    if (flash > 0) {
      flare(
        c, c.at.x + Math.cos(th) * r.rPx, c.at.y + Math.sin(th) * r.rPx,
        r.r2Px * flash, COMBAT_WHITE, RIPPLE_FLASH_ALPHA * flash,
      );
    }
    const meet = (c.p - RIPPLE_MEET_AT) / (1 - RIPPLE_MEET_AT);
    if (meet > 0) {
      const b = th + Math.PI;
      flare(
        c, c.at.x + Math.cos(b) * r.rPx, c.at.y + Math.sin(b) * r.rPx,
        r.lengthPx, SHIELD_INK, RIPPLE_MEET_ALPHA * (1 - meet),
      );
    }
  },

  /**
   * Card 03, PLATE LATTICE.
   *
   * The shield is a lattice of small hex plates over the hull. The plate under
   * the impact point lights full, its neighbours light a third, and the rest of
   * the lattice shows as hairlines.
   *
   * TWO TRANSLATIONS off the card. The canvas card clipped the lattice to the
   * contact ring; a batch has no clip, so a plate whose centre stands past
   * `LATTICE_CUT` of the ring is dropped and the plates themselves are the
   * edge. And the card FILLED a lit hex; the circle batch fills no hexagon, so
   * a lit plate takes a disc at the hex inradius under its own outline, which
   * touches all six sides.
   */
  'plate-lattice'(c) {
    const r = c.row;
    const f = 1 - c.p;
    const th = backOf(c);
    const ix = c.at.x + Math.cos(th) * r.rPx;
    const iy = c.at.y + Math.sin(th) * r.rPx;
    const hex = r.r2Px;
    const dx = hex * LATTICE_COL, dy = hex * LATTICE_ROW;
    const cut = r.rPx * LATTICE_CUT;
    for (let col = -r.count; col <= r.count; col++) {
      const px = col * dx;
      for (let row = -r.count2; row <= r.count2; row++) {
        const py = row * dy + (col & 1 ? dy / 2 : 0);
        if (Math.hypot(px, py) > cut) continue;
        const x = c.at.x + px, y = c.at.y + py;
        const dd = Math.hypot(x - ix, y - iy);
        const lit = dd < hex ? 1 : dd < LATTICE_NEAR * hex ? LATTICE_NEAR_LIT : 0;
        if (lit > 0) {
          disc(c, x, y, hex * LATTICE_INRADIUS, SHIELD_INK, lit * r.alpha * f);
        }
        hexOutline(
          c, x, y, hex, LATTICE_ROT, r.widthPx, SHIELD_INK,
          (r.alpha2 + LATTICE_LIT_RISE * lit) * f,
        );
      }
    }
    const flash = 1 - c.p / LATTICE_FLASH_SPAN;
    if (flash > 0) {
      flare(c, ix, iy, r.lengthPx, COMBAT_WHITE, LATTICE_FLASH_ALPHA * flash);
    }
  },

  /**
   * Card 05, GROWING CRACK.
   *
   * Cracks run inward from the impact point and grow while the mark lives, and
   * their LENGTH is the shield figure: one segment at a full shield and the
   * row's own `count2` segments near empty (R1011). A crack still grows at the
   * rate `speed` states, so the mark reads as a crack that opens and the
   * length it opens to reads as the shield that is left.
   */
  'growing-crack'(c) {
    const r = c.row;
    const f = tail(c.p, CRACK_FADE_AT);
    const th = backOf(c);
    const ix = c.at.x + Math.cos(th) * r.rPx;
    const iy = c.at.y + Math.sin(th) * r.rPx;
    ring(c, c.at.x, c.at.y, r.rPx, r.width2Px, SHIELD_INK, r.alpha2 * f);
    const inward = th + Math.PI;
    const mid = (r.count - 1) / 2;
    // One segment at a full shield, `count2` at an empty one. The card's own
    // `1 + floor(drain * 5.99)` over a `count2` of six.
    const cap = 1 + Math.floor(drain(c) * (r.count2 - CRACK_STEP_EDGE));
    for (let i = 0; i < r.count; i++) {
      const n = Math.min(cap, Math.floor(c.p * r.speed - i));
      if (n <= 0) continue;
      zig(
        c, ix, iy, inward + (i - mid) * CRACK_FAN, n, r.lengthPx, r.length2Px,
        c.seed + i, r.widthPx, SHIELD_INK, r.alpha * f,
      );
    }
    const flash = 1 - c.p / CRACK_FLASH_SPAN;
    if (flash > 0) {
      flare(c, ix, iy, r.r2Px, COMBAT_WHITE, CRACK_FLASH_ALPHA * flash);
    }
  },

  /**
   * Card 06, LOW FLICKER.
   *
   * The ring lights whole at the hit, and its alpha follows the shield figure.
   * Under `count2` percent of shield the ring and the bloom stutter at `speed`
   * strobes a second, so a shield about to fail reads before the bar does
   * (R1011). The threshold is the row's, because it is the figure a designer
   * moves; the card wrote 35 %.
   */
  'low-flicker'(c) {
    const r = c.row;
    const f = 1 - c.p;
    const th = backOf(c);
    const held = 1 - drain(c);
    const low = held < r.count2 / 100;
    const gate = low
      && hash(Math.floor(c.t * r.speed), FLICKER_ROW) >= 0.5 ? FLICKER_DIM : 1;
    // The whole ring states the shield: faint at an empty one, full at a full
    // one. The row's `alpha2` is the figure at a FULL shield.
    const whole = r.alpha2 * (FLICKER_RING_REST + (1 - FLICKER_RING_REST) * held);
    ring(c, c.at.x, c.at.y, r.rPx, r.width2Px, SHIELD_INK, whole * gate * f);
    arcOf(
      c, c.at.x, c.at.y, r.rPx, r.widthPx,
      th - FLICKER_ARC_HALF, FLICKER_ARC_HALF * 2, SHIELD_INK, r.alpha * gate * f,
    );
    flare(
      c, c.at.x + Math.cos(th) * r.rPx, c.at.y + Math.sin(th) * r.rPx,
      r.r2Px, SHIELD_INK, FLICKER_FLARE_ALPHA * gate * f,
    );
  },

  /**
   * Card 09, DRAIN SHIFT.
   *
   * Card 01 in an ink that follows the shield figure: the shield white at a
   * FULL shield, and the victim's own faction ink near empty (R1011). The mark
   * says how drained the shield is and never how old the mark is.
   *
   * THE ONE TRANSLATION. The card mixed toward the rust amber of the hull lane
   * because the stage handed it no target ink; the map has one, so the mix runs
   * to `c.ink`, which is the victim's faction (`fast/layers/combat.ts`
   * `spawnMark`).
   */
  'drain-shift'(c) {
    ringFlare(c, mixInk(SHIELD_INK, c.ink, drain(c)));
  },

  /**
   * Card 12, DOME SHEEN.
   *
   * The shield is a dome seen from above, clear at the centre and bright at the
   * rim. A lighter arc slides from the impact point over the dome to the far
   * side.
   *
   * The card built one radial gradient a frame and called itself expensive. The
   * kit's baked `dome` cell is that ramp, tinted and scaled by the quad, so the
   * look costs one sprite and bakes nothing new.
   */
  'dome-sheen'(c) {
    const r = c.row;
    const f = tail(c.p, SHEEN_FADE_AT);
    const th = backOf(c);
    dome(c, c.at.x, c.at.y, r.rPx, SHIELD_INK, r.alpha2 * f);
    ring(c, c.at.x, c.at.y, r.rPx, r.width2Px, SHIELD_INK, SHEEN_RIM_ALPHA * f);
    const a = th + Math.PI * out(c.p);
    arcOf(
      c, c.at.x, c.at.y, r.r2Px, r.widthPx,
      a - SHEEN_HALF, SHEEN_HALF * 2, SHIELD_INK, r.alpha * f,
    );
    const flash = 1 - c.p / SHEEN_FLASH_SPAN;
    if (flash > 0) {
      flare(
        c, c.at.x + Math.cos(th) * r.rPx, c.at.y + Math.sin(th) * r.rPx,
        r.lengthPx, COMBAT_WHITE, SHEEN_FLASH_ALPHA * flash,
      );
    }
  },

  /**
   * Card 14, RIM SPARKS.
   *
   * Sparks leave the impact point along the rim, half each way, and drift off
   * it as they slow and sink. The short arc under them lights. Sparks that hug
   * the ring say the shield, not the hull, took the round.
   */
  'rim-sparks'(c) {
    const r = c.row;
    const f = 1 - c.p;
    const th = backOf(c);
    const u = out(c.p);
    const sink = r.driftPx * c.p * c.p;
    for (let i = 0; i < r.count; i++) {
      const h1 = hash(c.seed, i * 2 + 1), h2 = hash(c.seed, i * 2 + 2);
      const s = i % 2 ? 1 : -1;
      const a = th + s * (SPARK_ANG_MIN + SPARK_ANG_SPAN * h1) * u;
      const rr = r.rPx + (r.lengthPx + r.length2Px * h2) * u;
      const ta = a - s * SPARK_TAIL;
      line(
        c,
        c.at.x + Math.cos(a) * rr, c.at.y + Math.sin(a) * rr + sink,
        c.at.x + Math.cos(ta) * (rr - SPARK_DROP),
        c.at.y + Math.sin(ta) * (rr - SPARK_DROP) + sink,
        r.widthPx, SHIELD_INK, r.alpha * f,
      );
    }
    arcOf(
      c, c.at.x, c.at.y, r.rPx, r.width2Px,
      th - SPARK_ARC_HALF, SPARK_ARC_HALF * 2, SHIELD_INK, r.alpha2 * f,
    );
    const flash = 1 - c.p / SPARK_FLASH_SPAN;
    if (flash > 0) {
      flare(
        c, c.at.x + Math.cos(th) * r.rPx, c.at.y + Math.sin(th) * r.rPx,
        r.r2Px, COMBAT_WHITE, SPARK_FLASH_ALPHA * flash,
      );
    }
  },

  /**
   * Card 15, INTERFERENCE.
   *
   * Ripples run over the dome from the impact point and stop at the rim, so the
   * shield reads as a surface with an edge.
   *
   * THE TRANSLATION of the card's clip, and it is exact. The card drew whole
   * rings round the impact point and clipped them to the shield disc. The
   * impact point stands ON the rim, so a ripple of radius d crosses the rim at
   * exactly ±acos(d / 2 r) off the line back to the hull centre: the recipe
   * draws that arc and no clip is wanted. The arc closes at d = 2 r, which is
   * where the card's own reach ends.
   */
  'interference'(c) {
    const r = c.row;
    const f = 1 - c.p;
    const th = backOf(c);
    const ix = c.at.x + Math.cos(th) * r.rPx;
    const iy = c.at.y + Math.sin(th) * r.rPx;
    const inward = th + Math.PI;
    const u = out(c.p);
    for (let i = 0; i < r.count; i++) {
      const d = r.lengthPx * u - i * r.length2Px;
      if (d <= 0) continue;
      const q = d / (2 * r.rPx);
      if (q >= 1) continue;
      const phi = Math.acos(q);
      arcOf(
        c, ix, iy, d, r.widthPx, inward - phi, phi * 2, SHIELD_INK,
        (r.alpha - WAVE_STEP_ALPHA * i) * f,
      );
    }
    ring(c, c.at.x, c.at.y, r.rPx, r.width2Px, SHIELD_INK, r.alpha2 * f);
    const flash = 1 - c.p / WAVE_FLASH_SPAN;
    if (flash > 0) {
      flare(c, ix, iy, r.r2Px, COMBAT_WHITE, WAVE_FLASH_ALPHA * flash);
    }
  },

  /**
   * Card 17, RIM ARCS.
   *
   * Two jagged arcs leave the impact point along the rim, one each way, and
   * flicker on every frame. The shortest life of the round.
   */
  'rim-arcs'(c) {
    const r = c.row;
    const f = 1 - c.p;
    const th = backOf(c);
    const n = r.count < ARC_MAX_STEPS ? r.count : ARC_MAX_STEPS;
    const frame = Math.floor(c.t * r.speed);
    const wind = ARC_STEP * (ARC_REST + ARC_RISE * f);
    for (let s = 1; s >= -1; s -= 2) {
      for (let i = 0; i <= n; i++) {
        // The two ends hold the rim; every joint between them wobbles off it,
        // on a hash the frame counter turns over.
        const wob = i > 0 && i < n
          ? (hash(frame * ARC_FRAME_MIX + s + 3, i) - 0.5) * r.lengthPx : 0;
        const a = th + s * i * wind;
        const rr = r.rPx + wob;
        ARC_XS[i] = c.at.x + Math.cos(a) * rr;
        ARC_YS[i] = c.at.y + Math.sin(a) * rr;
      }
      polyline(c, ARC_XS, ARC_YS, n + 1, r.widthPx, SHIELD_INK, r.alpha * f);
    }
    disc(
      c, c.at.x + Math.cos(th) * r.rPx, c.at.y + Math.sin(th) * r.rPx,
      r.r2Px * f, COMBAT_WHITE, r.alpha2,
    );
  },
};

/**
 * The cards of round 051 that wait for their own recipe, in card order.
 *
 * Each one draws RING FLARE until it is built. The mockup page
 * `archive/mockups/051-hits-shields.html` is the specification of each. The
 * owner picked ten of the twenty cards, and all ten now draw their own.
 */
export const PENDING: readonly string[] = [];

/**
 * Every id whose look draws the victim's hull itself. No shield mark does: the
 * mark stands on the contact ring, and the hull under it keeps its own
 * instance.
 */
export const OWNS_HULL: readonly string[] = [];
